/*
 * @file: index.js
 * @description: It Contain rest functions for api call .
 * @author: Poonam
 */

import axios from "axios";
import { setAuthorizationToken } from "../auth";
import { toast } from "react-toastify";
import loader from "../loader";
import environment from "../../environment";
import Swal from "sweetalert2";

const CLOVER_ERROR_CODES = [429, 503];
const CLOVER_ALERT_DURATION = 10000;

let isCloverAlertVisible = false;

const showCloverErrorAlert = () => {
  if (isCloverAlertVisible) return;

  isCloverAlertVisible = true;
  let countdownInterval;

  Swal.fire({
    icon: "warning",
    title: "Clover is temporarily unavailable",
    html: 'Clover is experiencing a temporary issue. Please<br />try again in <strong id="clover-error-countdown">10</strong> seconds.',
    timer: CLOVER_ALERT_DURATION,
    timerProgressBar: true,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      const countdown = Swal.getHtmlContainer()?.querySelector(
        "#clover-error-countdown"
      );

      countdownInterval = window.setInterval(() => {
        const timeLeft = Swal.getTimerLeft();
        if (countdown && timeLeft !== undefined) {
          countdown.textContent = String(
            Math.max(0, Math.ceil(timeLeft / 1000))
          );
        }
      }, 250);
    },
    willClose: () => window.clearInterval(countdownInterval),
  }).finally(() => {
    isCloverAlertVisible = false;
  });
};

var config = {
  headers: { "Content-Type": "application/json" },
};

var baseUrl = environment.api;

const getListingParams = (url = "", params = {}) => {
  const requestParams = params && typeof params === "object" ? params : {};
  const path = String(url).split("?")[0].replace(/\/+$/, "");
  const isListingRequest = /\/(?:list|listing)$/i.test(path);
  const queryHasSort = /[?&]sortBy=/i.test(String(url));
  const hasRequestedSort = String(requestParams.sortBy || "").trim();

  if (!isListingRequest || queryHasSort || hasRequestedSort) return requestParams;
  return { ...requestParams, sortBy: "name asc" };
};

const handleError = (err, hideError, statusCode) => {
  let message = "";
  const errorCode = Number(err?.error?.code || err?.code || statusCode);

  if (CLOVER_ERROR_CODES.includes(errorCode)) {
    showCloverErrorAlert();
    return;
  }

  if (err) {
    if (err && err.error && err.error.code == 401) {
      document.getElementById("logoutBtn")?.click()
      hideError = true;
    }
    message = err && err.error && err.error.message;
    if (!message) message = err.message;
    if (!message) message = "Server Error";
  }
  if (!hideError) toast.error(message);
};


class ApiClient {
  static post(url1, payload, params = {}, base = "", hideError = false) {
    let url = baseUrl + url1;
    if (base) url = base + url1;

    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      axios
        .post(url, JSON.stringify(payload), { ...config, params: params })
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, hideError, eres.status);
            fulfill({ ...eres.data, success: false });
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

  static put(url1, payload, base = "", hideError = false) {
    let url = baseUrl + url1;
    if (base) url = base + url1;
    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      axios
        .put(url, JSON.stringify(payload), config)
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, hideError, eres.status);
            fulfill(eres.data);
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

  static get(url1, params = {}, base = "", hidError = "") {
    let url = baseUrl + url1;
    if (base) url = base + url1;
    const requestParams = getListingParams(url1, params);
    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      axios
        .get(url, { ...config, params: requestParams })
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, hidError, eres.status);
            fulfill({ ...eres.data, success: false });
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

  static delete(url1, params = {}, base = "") {
    let url = baseUrl + url1;
    if (base) url = base + url1;

    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      axios
        .delete(url, { ...config, params: params })
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, false, eres.status);
            fulfill(eres.data);
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }


  static deleteWithBody(url1, payload, params = {}, base = "", hideError = false) {
    let url = baseUrl + url1;
    if (base) url = base + url1;

    setAuthorizationToken(axios);

    return new Promise(function (fulfill, reject) {
      axios
        .delete(url, {
          ...config,
          params: params,
          data: payload,
        })
        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, false, eres.status);
            fulfill(eres.data);
          } else {
            reject(error);
          }
        });
    });
  }
  static allApi(url, params, method = "get") {
    if (method == "get") {
      return this.get(url, params);
    } else if (method == "put") {
      return this.put(url, params);
    }
    if (method == "post") {
      return this.post(url, params);
    }
  }

  /*************** Form-Data Method ***********/
  static postFormData(url, params) {
    url = baseUrl + url;
    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      var body = new FormData();
      let oArr = Object.keys(params);
      oArr.map((itm) => {
        body.append(itm, params[itm]);
      });

      axios
        .post(url, body, config)

        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, false, eres.status);
            fulfill(eres.data);
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

  static postFormFileData(url, params) {
    let configupdate = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    url = baseUrl + url;
    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      var body = new FormData();
      let oArr = Object.keys(params);
      oArr.map((itm) => {
        body.append(itm, params[itm]);
      });

      axios
        .post(url, body, configupdate)

        .then(function (response) {
          fulfill(response && response.data);
        })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, false, eres.status);
            fulfill(eres.data);
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

  static multiImageUpload(url, files, params = {}, key = "file", setProgress = () => { }) {
    url = baseUrl + url;
    setAuthorizationToken(axios);
    let body = new FormData();

    let i = 0;
    for (let item of files) {
      let file = files.item(i);
      body.append(key, file);
      i++;
    }

    setAuthorizationToken(axios);
    return new Promise(function (fulfill, reject) {
      axios.post(url, body, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: params,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      }).then(function (response) {
        fulfill(response && response.data);
      })
        .catch(function (error) {
          loader(false);
          if (error && error.response) {
            let eres = error.response;
            handleError(eres.data, false, eres.status);
            fulfill(eres.data);
          } else {
            // toast.error("Network Error");
            reject(error);
          }
        });
    });
  }

}

export default ApiClient;
